import React from 'react'
import { Link } from 'react-router-dom'

import { LeavingGuard } from '@components/utils/LeavingGuard'
import type { SelectOption } from '@fourthwall/components'
import {
  Banner,
  ButtonClean,
  Checkbox,
  CurrencyInput,
  Fieldset,
  HeadContent,
  InputUI,
  Loading,
  Select,
  Subsection,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { useTiers } from '../../../Perks/hooks/useTiers'
import { useAccountSettingsQuery } from '../../hooks/useAccountSettingsQuery'
import { useUpdateAccountSettingsUpsellingsMutation } from '../../hooks/useUpdateAccountSettingsUpsellingsMutation'
import { validationSchema } from './consts'
import type { FormValues } from './types'
import { FormFields } from './types'
import { getUpdateAccountSettingsUpsellingsPayload } from './utils'

export const Upselling = () => {
  const { accountSettingsQuery } = useAccountSettingsQuery()
  const { updateAccountSettingsUpsellingsMutation } = useUpdateAccountSettingsUpsellingsMutation()
  const { tiersQuery, tiers } = useTiers()

  const formik = useFormik<FormValues>({
    initialValues: {
      enabled: !!accountSettingsQuery.data?.upselling,
      tier: accountSettingsQuery.data?.upselling
        ? accountSettingsQuery.data?.upselling.tierId.toString()
        : undefined,
      value: accountSettingsQuery.data?.upselling
        ? // eslint-disable-next-line no-unsafe-optional-chaining
          accountSettingsQuery.data?.upselling?.minimumOrderValue / 100
        : undefined,
    },
    validationSchema,
    onSubmit: (values) => {
      updateAccountSettingsUpsellingsMutation.mutate(
        [getUpdateAccountSettingsUpsellingsPayload(values)],
        {
          onSuccess: () => {
            accountSettingsQuery.refetch()
          },
        },
      )
    },
    enableReinitialize: true,
  })

  const isLoading =
    tiersQuery.isLoading ||
    accountSettingsQuery.isLoading ||
    updateAccountSettingsUpsellingsMutation.isLoading

  const getSelectOptions = (): SelectOption[] => {
    if (!tiers) return []

    const options: SelectOption[] = tiers.map((tier) => ({
      key: tier.id.toString(),
      value: tier.id.toString(),
      label: tier.title,
    }))

    return options
  }

  const renderContent = () => {
    if (!tiers && !tiersQuery.isLoading) {
      return (
        <Fieldset.Row>
          <Fieldset.Item>
            <Banner
              title={
                <>
                  You need to have <Link to={routing.memberships.perks.self}>membership tiers</Link>{' '}
                  created to enable this offer.
                </>
              }
              appearance="warning"
            />
          </Fieldset.Item>
        </Fieldset.Row>
      )
    }

    return (
      <>
        <Fieldset.Row>
          <Fieldset.Item>
            <Checkbox
              name={FormFields.enabled}
              value={formik.values.enabled}
              label="Offer free month of memberships with shop purchase"
              onChange={formik.handleChange}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        {formik.values.enabled && (
          <>
            <Fieldset.Row inner>
              <Fieldset.Item>
                <Select
                  name={FormFields.tier}
                  label="Membership tier"
                  options={getSelectOptions()}
                  value={formik.values.tier}
                  onChange={formik.handleChange}
                />
                {formik.errors.tier && <InputUI.Error>{formik.errors.tier}</InputUI.Error>}
              </Fieldset.Item>
              <Fieldset.Item>
                <CurrencyInput
                  name={FormFields.value}
                  value={formik.values.value?.toString()}
                  label="Minimum order value"
                  onChange={formik.handleChange}
                />
                {formik.errors.value && <InputUI.Error>{formik.errors.value}</InputUI.Error>}
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row inner>
              <InputUI.Suggestion $withoutMargin>
                Combine this offer with members-only discounts to boost sales.
                <br />
                <ButtonClean
                  label="Learn more"
                  href="https://help.fourthwall.com/hc/en-us/articles/20770026695323#:~:text=Members%2Donly%20discounts"
                  appearance="secondary"
                  target="_blank"
                  withUnderline
                />
              </InputUI.Suggestion>
            </Fieldset.Row>
          </>
        )}
      </>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <HeadContent title="Shop upselling" />
      <Loading isLoading={isLoading}>
        <Subsection title="Offer free month of memberships with shop purchase">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                When your supporters spend a certain amount at your shop, they will be presented
                with the option to add free month of membership during checkout. Promotion applies
                only to new members.{' '}
                <ButtonClean
                  label="Learn more"
                  href="https://help.fourthwall.com/hc/en-us/articles/20770026695323"
                  target="_blank"
                  withUnderline
                />
              </Fieldset.Item>
            </Fieldset.Row>
            {renderContent()}
          </Fieldset>
        </Subsection>
      </Loading>
      <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
      <ConfirmBar
        isOpen={formik.dirty}
        isLoading={isLoading}
        onConfirm={formik.submitForm}
        onCancel={formik.resetForm}
      />
      <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
    </form>
  )
}
